import styled from '@emotion/styled';
import { Button, ButtonProps, Flex } from 'antd';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { ArrowIcon } from '../Icon';

interface FilterCollapsibleProps {
  children: React.ReactNode;
  stickElement?: React.ReactNode;
}

const CollapsibleContainer = styled.div<{ height: number }>`
  position: relative;
  overflow: hidden;
  transition: height 0.3s ease;
  height: ${({ height }) => height}px;
`;

interface StyledButtonProps extends ButtonProps {
  collapsed: string;
}

const StyledButton = styled(Button)<StyledButtonProps>`
  position: absolute;
  left: 50%;
  bottom: -10px;
  transform: translateX(-50%);
  > svg {
    transition: transform 0.3s;
    transform: ${({ collapsed }) =>
      `rotate(${collapsed === 'true' ? 180 : 0}deg)`};
  }
`;

const Container = styled.div`
  position: relative;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fafafa;
  padding: 12px;
  padding-bottom: 20px;
`;

interface CollapseArrowProps {
  onClick: () => void;
  collapsed: boolean;
}

const CollapseArrow: React.FC<CollapseArrowProps> = ({
  onClick,
  collapsed,
}) => {
  return (
    <StyledButton
      collapsed={String(collapsed)}
      type="primary"
      shape="circle"
      size="small"
      onClick={onClick}
    >
      <ArrowIcon />
    </StyledButton>
  );
};

export const FilterCollapsible: React.FC<FilterCollapsibleProps> = ({
  children,
  stickElement,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined); // undefined for initial load state
  const isOpen = height !== 0;

  // Initial expanded height on mount
  useLayoutEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight); // Set expanded height initially
    }
  }, []);

  // Adjust height dynamically on content change
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (contentRef.current) {
        setHeight(isOpen ? contentRef.current.scrollHeight : 0);
      }
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [isOpen, children]);

  const toggleHeight = () => {
    setHeight(
      isOpen ? 0 : contentRef.current ? contentRef.current.scrollHeight : 0
    );
  };

  const stickEl = stickElement ?? (
    <Flex justify="center">
      <span className="font-semibold">Filter</span>
    </Flex>
  );

  return (
    <Container>
      <CollapsibleContainer height={height ?? 0}>
        <div ref={contentRef}>{children}</div>
      </CollapsibleContainer>
      {!isOpen && stickEl}
      <CollapseArrow onClick={toggleHeight} collapsed={isOpen} />
    </Container>
  );
};
